import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { useT, useLang, usePageTitle, Trans } from "@/i18n/LanguageContext";
import useScrollReveal from "@/hooks/useScrollReveal";


// Fallback when no products are hand-picked in the admin Homepage tab:
// featured-flagged products, topped up with the newest ones to reach 3.
async function loadFallbackFeatured() {
    const r = await api.get("/products", { params: { featured: true, visible: true } });
    const items = [...(r.data.items ?? [])];
    if (items.length < 3) {
        try {
            const extra = await api.get("/products", { params: { visible: true, page_size: 6 } });
            const seen = new Set(items.map((p) => p.id));
            for (const p of extra.data.items ?? []) {
                if (items.length >= 3) break;
                if (!seen.has(p.id)) { items.push(p); seen.add(p.id); }
            }
        } catch { /* featured alone is fine */ }
    }
    return items;
}

// "Piese alese" spotlight: on desktop a serif name-list drives a large crossfading
// stage image (hover to switch, gentle auto-rotation); on phones, editorial cards.
function FeaturedShowcase({ products }) {
    const t = useT();
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (active >= products.length) setActive(0);
    }, [products.length, active]);

    useEffect(() => {
        if (products.length < 2 || paused) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const id = setInterval(() => setActive((a) => (a + 1) % products.length), 5000);
        return () => clearInterval(id);
    }, [products.length, paused]);

    const cur = (p, v) => (p.currency === "MDL" ? `${v} MDL` : `€${v}`);
    const activeProduct = products[active] || products[0];
    if (!activeProduct) return null;

    return (
        <>
            {/* Desktop: interactive spotlight */}
            <div
                className="hidden md:grid grid-cols-12 gap-10 lg:gap-14 items-center"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                data-reveal
                data-testid="featured-showcase"
            >
                <div className="col-span-5 flex flex-col divide-y divide-white/10">
                    {products.map((p, i) => (
                        <Link
                            key={p.id}
                            to={`/product/${p.id}`}
                            onMouseEnter={() => setActive(i)}
                            onFocus={() => setActive(i)}
                            data-testid={`featured-item-${p.id}`}
                            className={`group py-7 lg:py-8 flex items-baseline gap-6 transition-opacity duration-500 ${
                                i === active ? "opacity-100" : "opacity-35 hover:opacity-70"
                            }`}
                        >
                            <span className="font-serif text-lg text-melilux-gold shrink-0">0{i + 1}</span>
                            <div className="min-w-0">
                                <h3 className="font-serif text-3xl lg:text-[2.6rem] leading-[1.05] text-melilux-cream capitalize group-hover:text-melilux-gold transition-colors">
                                    {p.name}
                                </h3>
                                <div className="mt-3 flex items-baseline gap-4 flex-wrap text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/50">
                                    <span>{p.category_name || p.room}</span>
                                    {p.price && (
                                        <span className="flex items-baseline gap-2">
                                            {p.sale_price && (
                                                <span className="line-through opacity-50">{cur(p, p.price)}</span>
                                            )}
                                            <span className={p.sale_price ? "text-melilux-gold" : ""}>
                                                {cur(p, p.sale_price || p.price)}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <ArrowUpRight
                                size={18}
                                className={`ml-auto shrink-0 self-center text-melilux-gold transition-all duration-500 ${
                                    i === active ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"
                                }`}
                            />
                        </Link>
                    ))}
                </div>

                <Link
                    to={`/product/${activeProduct.id}`}
                    className="col-span-7 relative aspect-[4/3] overflow-hidden bg-melilux-surface group block"
                    data-testid="featured-stage"
                >
                    {products.map((p, i) => (
                        <img
                            key={p.id}
                            src={resolveImg(p.images?.[0], 800)}
                            alt={i === active ? p.name : ""}
                            loading={i === 0 ? "eager" : "lazy"}
                            decoding="async"
                            className={`absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-out ${
                                i === active ? "opacity-100 scale-100" : "opacity-0 scale-[1.06]"
                            }`}
                        />
                    ))}
                    {activeProduct.sale_percentage && (
                        <div className="absolute top-4 right-4 flex items-center justify-center h-7 px-3 bg-melilux-gold">
                            <span className="text-[0.62rem] leading-none uppercase tracking-[0.3em] -mr-[0.3em] text-melilux-bg font-medium">
                                −{activeProduct.sale_percentage}%
                            </span>
                        </div>
                    )}
                    <div className="absolute bottom-4 left-5 text-[0.65rem] uppercase tracking-[0.3em] text-melilux-cream/80 bg-melilux-bg/50 backdrop-blur px-3 py-2">
                        0{active + 1} / 0{products.length}
                    </div>
                </Link>
            </div>

            {/* Mobile: editorial cards with caption on a dark scrim */}
            <div className="md:hidden space-y-5">
                {products.map((p, i) => (
                    <Link
                        key={p.id}
                        to={`/product/${p.id}`}
                        data-testid={`featured-card-${p.id}`}
                        data-reveal
                        style={{ "--reveal-delay": `${i * 110}ms` }}
                        className="group relative block overflow-hidden bg-melilux-surface aspect-[4/3]"
                    >
                        {p.images?.[0] && (
                            <img
                                src={resolveImg(p.images[0], 800)}
                                alt={p.name}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.05]"
                            />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                        {p.sale_percentage && (
                            <div className="absolute top-3 right-3 flex items-center justify-center h-7 px-3 bg-melilux-gold">
                                <span className="text-[0.62rem] leading-none uppercase tracking-[0.3em] -mr-[0.3em] text-melilux-bg font-medium">
                                    −{p.sale_percentage}%
                                </span>
                            </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 p-5">
                            <p className="overline">0{i + 1} · {p.category_name || p.room}</p>
                            <h3 className="font-serif text-2xl text-white capitalize mt-1.5 leading-tight">{p.name}</h3>
                            {p.price && (
                                <p className="mt-1.5 flex items-baseline gap-3 text-[0.7rem] uppercase tracking-[0.2em] text-melilux-cream/70">
                                    {p.sale_price && <span className="line-through opacity-50">{cur(p, p.price)}</span>}
                                    <span className={p.sale_price ? "text-melilux-gold" : ""}>{cur(p, p.sale_price || p.price)}</span>
                                </p>
                            )}
                        </div>
                    </Link>
                ))}
            </div>
        </>
    );
}

export default function Home() {
  const t = useT();
  const { lang } = useLang();
  usePageTitle(t("meta.title.home"));
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [galleryVisible, setGalleryVisible] = useState(12);
  const [heroImage, setHeroImage] = useState(null);
  const [heroOverride, setHeroOverride] = useState(null);
  const [experienceOverride, setExperienceOverride] = useState(null);
  const [heroTagline, setHeroTagline] = useState(null);
  const heroParallaxRef = useRef(null);

  useScrollReveal([categories, featured, gallery, galleryVisible]);

  useEffect(() => {
      api.get("/categories", { params: { top_level: true } }).then((r) => {
          const FEATURED_SLUGS = ["dormitoare", "camere-de-zi", "mese-si-scaune"];
          const ordered = FEATURED_SLUGS
              .map((slug) => r.data.find((c) => c.slug === slug))
              .filter(Boolean);
          setCategories(ordered);
      }).catch(() => {});

    // Admin-curated homepage: hand-picked featured products + projects gallery.
    api.get("/homepage")
        .then(async (r) => {
            setGallery((r.data.gallery_products ?? []).filter((p) => p.images?.[0]));
            if (r.data.hero_image) setHeroOverride(resolveImg(r.data.hero_image, 1200));
            if (r.data.experience_image) setExperienceOverride(resolveImg(r.data.experience_image, 1200));
            setHeroTagline(r.data.hero_tagline ?? null);
            const chosen = r.data.products ?? [];
            setFeatured(chosen.length ? chosen : await loadFallbackFeatured());
        })
        .catch(() => {
            loadFallbackFeatured().then(setFeatured).catch(() => {});
        });
  }, []);

    useEffect(() => {
        api.get("/products/by-slug/zenith-2")
            .then((r) => {
                const imgs = r.data.images || [];
                const src = imgs[3] || imgs[0];
                if (src) setHeroImage(resolveImg(src, 1200));
            })
            .catch(() => {});
    }, []);

    // Subtle parallax drift on the hero image while scrolling past it.
    useEffect(() => {
        const el = heroParallaxRef.current;
        if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const y = Math.min(window.scrollY, window.innerHeight);
                el.style.transform = `translateY(${y * 0.12}px)`;
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [heroImage]);

    // Admin override wins; then the dedicated hero product; then the first collection cover.
    const heroSrc = heroOverride || heroImage || (categories[0]?.cover_image ? resolveImg(categories[0].cover_image, 1200) : null);
    // Admin-edited text over the hero photo; untranslated languages fall back to Romanian.
    const heroTaglineText = heroTagline?.[lang] || heroTagline?.ro || "";
    const experienceSrc = experienceOverride || (categories[1]?.cover_image ? resolveImg(categories[1].cover_image, 1200) : null);

  return (
    <>
      {/* HERO */}
      <section className="relative -mt-20 hero-viewport min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
          {heroSrc && (
              <div ref={heroParallaxRef} className="absolute inset-x-0 -top-[15%] bottom-0 will-change-transform">
                  <img
                      src={heroSrc}
                      alt=""
                      fetchPriority="high"
                      className="w-full h-full object-cover hero-kenburns"
                  />
              </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-melilux-bg via-melilux-bg/75 to-melilux-bg/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-melilux-bg via-transparent to-melilux-bg/60" />
          <div className="absolute inset-0 bg-melilux-bg/20 grain" />

          {/* Admin-edited tagline, set on the clear part of the photo: top-left on phones, right side on desktop */}
          {heroTaglineText && (
              <div
                  className="absolute z-10 top-28 left-6 right-6 md:left-auto md:right-10 md:top-[30%] md:max-w-[34%] md:text-right reveal reveal-delay-2"
                  data-testid="hero-tagline"
              >
                  <span className="block w-12 h-px bg-melilux-gold md:ml-auto" aria-hidden="true" />
                  <p className="serif-italic text-[2rem] md:text-4xl lg:text-5xl leading-[1.05] text-melilux-cream mt-4 [text-shadow:0_2px_24px_rgba(0,0,0,0.65)]">
                      {heroTaglineText}
                  </p>
              </div>
          )}

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-28">
              <div className="md:max-w-[60%]">
                  <p className="overline reveal" data-testid="hero-overline">{t("hero.overline")}</p>
                  <h1 className="font-serif text-[2.7rem] sm:text-6xl lg:text-[5.5rem] leading-[0.92] tracking-tight max-w-5xl mt-6 reveal reveal-delay-1">
                      <Trans tKey="hero.title" />
                  </h1>
                  <p className="text-melilux-cream/70 max-w-xl text-base md:text-lg mt-8 leading-relaxed reveal reveal-delay-2">
                      {t("hero.lead")}
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4 reveal reveal-delay-3">
            <Link
              to="/collections"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-3 bg-melilux-cream text-melilux-bg px-8 py-4 text-[0.72rem] uppercase tracking-[0.3em] font-medium hover:bg-melilux-gold transition-colors"
            >
              {t("hero.exploreBtn")}
              <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-visit-btn"
              className="inline-flex items-center gap-3 border border-melilux-cream/40 text-melilux-cream px-8 py-4 text-[0.72rem] uppercase tracking-[0.3em] font-medium hover:border-melilux-gold hover:text-melilux-gold transition-colors"
            >
              {t("hero.bookBtn")}
            </Link>
          </div>
              </div>
        </div>

        <div className="hidden lg:block absolute right-10 bottom-28 text-right">
          <div className="overline mb-3">{t("hero.established")}</div>
          <div className="font-serif text-5xl text-melilux-cream">MMXIII</div>
          <div className="overline mt-3">{t("hero.location")}</div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 hidden md:block" aria-hidden="true">
          <span className="block w-px h-12 bg-melilux-gold/70 scroll-cue" />
        </div>
      </section>

      {/* FEATURED COLLECTIONS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="collections-section">
        <div className="grid grid-cols-12 gap-y-6 items-end mb-16">
          <div className="col-span-12 md:col-span-7" data-reveal>
            <p className="overline">{t("feat.overline")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
              <Trans tKey="feat.title" />
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9" data-reveal style={{ "--reveal-delay": "120ms" }}>
            <p className="text-melilux-cream/60 leading-relaxed">{t("feat.lead")}</p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {categories.map((c, idx) => (
            <Link
              to={`/collections/${c.slug}`}
              key={c.id}
              data-testid={`collection-card-${c.slug}`}
              data-reveal
              style={{ "--reveal-delay": `${idx * 110}ms` }}
              className={`group relative hover-zoom block bg-melilux-surface ${
                idx === 0
                  ? "col-span-12 md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-[5/6]"
                  : "col-span-12 md:col-span-5 aspect-[5/4]"
              }`}
            >
                {c.cover_image && (
                    <img src={resolveImg(c.cover_image, 1200)} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-melilux-bg via-melilux-bg/40 to-transparent" />
              <div className="absolute inset-0 p-7 md:p-10 flex flex-col justify-end">
                <span className="overline">{t("feat.card")} · 0{idx + 1}</span>
                <div className="flex items-end justify-between mt-2">
                  <h3 className="font-serif text-3xl md:text-5xl text-melilux-cream">{c.name}</h3>
                  <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-melilux-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {t("feat.discover")} <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="relative" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid grid-cols-12 gap-y-10 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-5 relative aspect-[3/4] bg-melilux-surface" data-reveal>
              {experienceSrc && (
                  <img src={experienceSrc} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              )}
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7" data-reveal style={{ "--reveal-delay": "140ms" }}>
            <p className="overline">{t("exp.overline")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
              <Trans tKey="exp.title" />
            </h2>
            <p className="text-melilux-cream/65 mt-8 leading-relaxed max-w-lg">{t("exp.lead")}</p>

            <div className="mt-12 grid grid-cols-3 gap-8 border-t border-white/10 pt-10">
              {[
                { k: "2013", v: t("exp.atelier") },
                { k: "100%", v: t("exp.pieces") },
                { k: `${new Date().getFullYear() - 2013}+`, v: t("exp.warranty") },
              ].map((s, idx) => (
                <div key={s.v} data-reveal style={{ "--reveal-delay": `${200 + idx * 110}ms` }}>
                  <div className="font-serif text-3xl md:text-4xl text-melilux-gold">{s.k}</div>
                  <div className="text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/60 mt-2">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PIECES */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="featured-products">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6" data-reveal>
            <div>
              <p className="overline">{t("sel.overline")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">{t("sel.title")}</h2>
            </div>
            <Link to="/collections" data-testid="view-all-link" className="text-[0.72rem] uppercase tracking-[0.28em] text-melilux-gold link-underline">
              {t("sel.viewAll")}
            </Link>
          </div>

          <FeaturedShowcase products={featured.slice(0, 3)} />
        </section>
      )}

      {/* COMPLETED PROJECTS GALLERY */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="projects-gallery">
          <div className="mb-12" data-reveal>
            <p className="overline">{t("home.gallery.overline")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
              <Trans tKey="home.gallery.title" />
            </h2>
          </div>

          {/* Editorial mosaic: repeating pattern of large, tall, wide and square tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[150px] sm:auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4 grid-flow-dense">
            {gallery.slice(0, galleryVisible).map((p, idx) => {
              const m = idx % 8;
              const span =
                m === 0 ? "col-span-2 row-span-2" : // large feature tile
                m === 3 ? "row-span-2" :            // tall
                m === 5 ? "col-span-2" :            // wide
                "";                                  // square
              return (
                <Link
                  key={p.id}
                  to={`/collections/${p.category_slug}/${p.slug}`}
                  data-testid={`gallery-img-${idx}`}
                  data-reveal
                  style={{ "--reveal-delay": `${(idx % 4) * 90}ms` }}
                  className={`group relative block overflow-hidden bg-melilux-surface ${span}`}
                >
                  <img
                    src={resolveImg(p.images[0], m === 0 ? 800 : 480)}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.06]"
                  />
                  {/* Dark scrim so the hover caption stays readable on bright photos */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="font-serif text-lg md:text-xl text-white capitalize leading-tight">{p.name}</p>
                    <span className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.25em] text-melilux-gold mt-1.5">
                      {t("feat.discover")} <ArrowUpRight size={11} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {gallery.length > galleryVisible && (
            <div className="mt-12 text-center">
              <button
                type="button"
                onClick={() => setGalleryVisible((v) => v + 12)}
                data-testid="gallery-show-more"
                className="inline-flex items-center gap-3 border border-melilux-gold/60 text-melilux-gold px-10 py-4 text-[0.72rem] uppercase tracking-[0.3em] hover:bg-melilux-gold hover:text-melilux-bg transition-colors"
              >
                {t("home.gallery.more")}
                <span className="text-melilux-gold/50">{Math.min(galleryVisible, gallery.length)} / {gallery.length}</span>
              </button>
            </div>
          )}
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="cta-section">
        <div className="border-t border-b border-white/10 py-20 md:py-28 flex flex-col items-center text-center" data-reveal>
          <p className="overline">{t("cta.overline")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-3xl leading-[0.95]">
            <Trans tKey="cta.title" />
          </h2>
          <Link
            to="/contact"
            data-testid="cta-book-btn"
            className="mt-10 bg-melilux-gold text-melilux-bg px-10 py-4 text-[0.72rem] uppercase tracking-[0.3em] font-medium hover:bg-melilux-goldBright transition-colors"
          >
            {t("cta.button")}
          </Link>
        </div>
      </section>
    </>
  );
}
